import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { updateProfile, updateEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider, signOut } from 'firebase/auth';
import { auth, db, storage } from '../../firebase/firebaseConfig';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { compressImage } from '../../utils/imageUtils';

const ProfileSettings = () => {
  const { t } = useTranslation();
  const { currentUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Profile form state
  const [profileForm, setProfileForm] = useState({
    displayName: '',
    email: '',
    phone: '',
    photoURL: ''
  });
  
  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Settings state
  const [settings, setSettings] = useState({
    notifications: true,
    language: 'th'
  });
  
  // Error states
  const [errors, setErrors] = useState({});
  const [showChangePassword, setShowChangePassword] = useState(false);

  useEffect(() => {
    if (currentUser) {
      loadUserData();
    } else if (currentUser === null) {
      // Only redirect if user is explicitly null (not loading)
      navigate('/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const loadUserData = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      // Load from Firebase Auth
      const authUser = auth.currentUser;
      if (authUser) {
        setProfileForm({
          displayName: authUser.displayName || '',
          email: authUser.email || '',
          phone: '',
          photoURL: authUser.photoURL || ''
        });
      }

      // Load from Firestore
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        setProfileForm(prev => ({
          ...prev,
          phone: userData.phone || userData.phoneNumber || '',
          photoURL: userData.photoURL || prev.photoURL
        }));
        setSettings({
          notifications: userData.notificationsEnabled !== false,
          language: userData.language || 'th'
        });
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      alert(t('settings.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSettingsChange = (name, value) => {
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      alert(t('settings.fileTypeError'));
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      alert(t('settings.fileSizeError'));
      return;
    }

    setUploading(true);
    try {
      // Compress image (max 800x800, quality 0.8)
      const compressedFile = await compressImage(file, 800, 800, 0.8);
      
      // Upload to Firebase Storage
      const storageRef = ref(storage, `profile-images/${currentUser.uid}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, compressedFile);
      const downloadURL = await getDownloadURL(storageRef);

      // Update profile form
      setProfileForm(prev => ({
        ...prev,
        photoURL: downloadURL
      }));

      alert(t('settings.uploadSuccess'));
    } catch (error) {
      console.error('Error uploading avatar:', error);
      if (error.code === 'storage/unauthorized') {
        alert(t('settings.uploadUnauthorized'));
      } else {
        alert(t('settings.uploadError') + error.message);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!currentUser) return;

    setSaving(true);
    const newErrors = {};

    // Validate
    if (!profileForm.displayName || profileForm.displayName.trim() === '') {
      newErrors.displayName = t('settings.fullNameRequired');
    }

    if (!profileForm.email || profileForm.email.trim() === '') {
      newErrors.email = t('settings.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileForm.email)) {
      newErrors.email = t('settings.emailInvalid');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setSaving(false);
      return;
    }

    try {
      const authUser = auth.currentUser;
      if (!authUser) {
        throw new Error(t('settings.userNotFound'));
      }

      // Update Firebase Auth profile
      const updates = {};
      if (profileForm.displayName !== authUser.displayName) {
        updates.displayName = profileForm.displayName;
      }
      if (profileForm.photoURL !== authUser.photoURL) {
        updates.photoURL = profileForm.photoURL;
      }

      if (Object.keys(updates).length > 0) {
        await updateProfile(authUser, updates);
      }

      // Update email if changed
      if (profileForm.email !== authUser.email) {
        try {
          await updateEmail(authUser, profileForm.email);
        } catch (emailError) {
          if (emailError.code === 'auth/requires-recent-login') {
            alert(t('settings.emailChangeWarning'));
            return;
          }
          throw emailError;
        }
      }

      // Update Firestore
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        displayName: profileForm.displayName,
        email: profileForm.email,
        phone: profileForm.phone,
        photoURL: profileForm.photoURL,
        notificationsEnabled: settings.notifications,
        language: settings.language,
        updatedAt: new Date()
      });

      // Refresh user data
      await refreshUser(authUser);

      alert(t('settings.saveSuccess'));
    } catch (error) {
      console.error('Error saving profile:', error);
      if (error.code === 'auth/requires-recent-login') {
        alert(t('settings.reloginWarning'));
      } else {
        alert(t('settings.saveError') + error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentUser) return;

    const newErrors = {};

    // Validate
    if (!passwordForm.currentPassword) {
      newErrors.currentPassword = t('settings.currentPasswordRequired');
    }

    if (!passwordForm.newPassword) {
      newErrors.newPassword = t('settings.newPasswordRequired');
    } else if (passwordForm.newPassword.length < 6) {
      newErrors.newPassword = t('settings.passwordMinLength');
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = t('settings.passwordMismatch');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    try {
      const authUser = auth.currentUser;
      if (!authUser || !authUser.email) {
        throw new Error(t('settings.userNotFound'));
      }

      // Re-authenticate user
      const credential = EmailAuthProvider.credential(
        authUser.email,
        passwordForm.currentPassword
      );
      await reauthenticateWithCredential(authUser, credential);

      // Update password
      await updatePassword(authUser, passwordForm.newPassword);

      // Clear form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowChangePassword(false);

      alert(t('settings.passwordChangeSuccess'));
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        setErrors({ currentPassword: t('settings.currentPasswordIncorrect') });
      } else if (error.code === 'auth/weak-password') {
        setErrors({ newPassword: t('settings.passwordWeak') });
      } else {
        alert(t('settings.saveError') + error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    if (window.confirm(t('settings.confirmLogout'))) {
      try {
        await signOut(auth);
        navigate('/login');
      } catch (error) {
        console.error('Error logging out:', error);
        alert(t('settings.logoutError'));
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-2xl">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('common.back')}
          </button>
          <h1 className="text-3xl font-bold text-gray-800">{t('settings.title')}</h1>
        </div>

        {/* Profile Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">{t('settings.profileInfo')}</h2>
          
          {/* Avatar */}
          <div className="flex flex-col items-center mb-6">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                {profileForm.photoURL ? (
                  <img src={profileForm.photoURL} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-4xl font-bold text-gray-400">
                    {profileForm.displayName?.charAt(0) || 'U'}
                  </span>
                )}
              </div>
              <label className="absolute bottom-0 right-0 bg-green-500 text-white p-2 rounded-full cursor-pointer hover:bg-green-600 transition">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            </div>
            {uploading && (
              <p className="text-sm text-gray-500 mt-2">{t('common.uploading')}</p>
            )}
            <p className="text-sm text-gray-500 mt-2">{t('settings.clickToChangeImage')}</p>
          </div>

          {/* Display Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('settings.fullName')} *
            </label>
            <input
              type="text"
              name="displayName"
              value={profileForm.displayName}
              onChange={handleProfileChange}
              className={`w-full px-4 py-2 border rounded-lg ${
                errors.displayName ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={t('settings.fullNamePlaceholder')}
            />
            {errors.displayName && (
              <p className="text-red-500 text-sm mt-1">{errors.displayName}</p>
            )}
          </div>

          {/* Email */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('settings.email')} *
            </label>
            <input
              type="email"
              name="email"
              value={profileForm.email}
              onChange={handleProfileChange}
              className={`w-full px-4 py-2 border rounded-lg ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={t('settings.emailPlaceholder')}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              ⚠️ {t('settings.emailChangeNote')}
            </p>
          </div>

          {/* Phone */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('settings.phone')}
            </label>
            <input
              type="tel"
              name="phone"
              value={profileForm.phone}
              onChange={handleProfileChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              placeholder={t('settings.phonePlaceholder')}
            />
          </div>
        </div>

        {/* Account Settings */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">{t('settings.accountSettings')}</h2>
          
          {/* Change Password */}
          <div className="mb-4">
            <button
              onClick={() => setShowChangePassword(!showChangePassword)}
              className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition flex items-center justify-between"
            >
              <span className="font-medium text-gray-700">{t('settings.changePassword')}</span>
              <svg className={`w-5 h-5 text-gray-500 transition-transform ${showChangePassword ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showChangePassword && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('settings.currentPassword')} *
                  </label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordChange}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      errors.currentPassword ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={t('settings.currentPasswordPlaceholder')}
                  />
                  {errors.currentPassword && (
                    <p className="text-red-500 text-sm mt-1">{errors.currentPassword}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('settings.newPassword')} *
                  </label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      errors.newPassword ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={t('settings.newPasswordPlaceholder')}
                  />
                  {errors.newPassword && (
                    <p className="text-red-500 text-sm mt-1">{errors.newPassword}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('settings.confirmPassword')} *
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={t('settings.confirmPasswordPlaceholder')}
                  />
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
                  )}
                </div>
                
                <button
                  onClick={handleChangePassword}
                  disabled={saving}
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors disabled:bg-gray-400"
                >
                  {saving ? t('settings.changing') : t('settings.changePassword')}
                </button>
              </div>
            )}
          </div>

          {/* Notifications */}
          <div className="mb-4">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg">
              <div>
                <span className="font-medium text-gray-700">{t('settings.notifications')}</span>
                <p className="text-sm text-gray-500">{t('settings.notificationsDescription')}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.notifications}
                  onChange={(e) => handleSettingsChange('notifications', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
              </label>
            </div>
          </div>

          {/* Language */}
          <div className="mb-4">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg">
              <div>
                <span className="font-medium text-gray-700">{t('settings.language')}</span>
                <p className="text-sm text-gray-500">{t('settings.selectLanguage')}</p>
              </div>
              <select
                value={settings.language}
                onChange={(e) => handleSettingsChange('language', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg bg-white"
              >
                <option value="th">{t('settings.thai')}</option>
                <option value="en">{t('settings.english')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Other Options */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">{t('settings.others')}</h2>
          
          <div className="space-y-2">
            <button className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition">
              <span className="font-medium text-gray-700">{t('settings.termsOfService')}</span>
            </button>
            
            <button className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition">
              <span className="font-medium text-gray-700">{t('settings.privacyPolicy')}</span>
            </button>
            
            <button
              onClick={handleLogout}
              className="w-full text-left px-4 py-3 bg-red-50 hover:bg-red-100 rounded-lg transition"
            >
              <span className="font-medium text-red-700">{t('settings.logout')}</span>
            </button>
          </div>
        </div>

        {/* Save Button */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 -mx-4">
          <button
            onClick={handleSaveProfile}
            disabled={saving}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:bg-gray-400"
          >
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;

